// @ts-expect-error
import type {Control} from 'react-hook-form';
// @ts-expect-error
import {useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {Body} from 'shells/typography/body';

import type {FormFieldArrayRow, MultiPageForm} from '../../../types';

import type {NutshellState} from '../../../../../../../store';
import {getIcon, getTitle, fieldSortFunction} from '../../../../../../core/nutshell-fields/helpers';
import type {FormSchemaProperty} from '../../../../../../core/nutshell-fields/types';
import {getFormSchemaFields} from '../../../../../forms-selectors';
import type {FormFieldRowForDuplicateHelpers} from '../../../../../types';
import {getDisabledText, getFilteredFields} from '../../../../editor/preview/add-card/helpers';

import {AddableFieldCard} from './addable-field-card';
import {FieldCardPreview} from './field-card-preview';

import styles from './content-blocks-tab.module.css';

interface Props {
    control: Control<MultiPageForm>;
}

export function NutshellFieldsSection(props: Props) {
    const schemaFields = useSelector((state: NutshellState) => getFormSchemaFields(state));

    const watchedPages: MultiPageForm['pages'] = useWatch({
        control: props.control,
        name: 'pages',
    });

    const allCurrentFields = (watchedPages ?? []).reduce<FormFieldArrayRow[]>(
        (acc, page) => acc.concat(page.fields ?? []),
        []
    );

    const fieldsForDuplicateHelpers = allCurrentFields as FormFieldRowForDuplicateHelpers[];

    // Some fields are only able to be added once, meaning that we monitor the
    // pages to disable further additions if included
    const filteredFields = getFilteredFields({
        schemaFields,
        fields: fieldsForDuplicateHelpers,
        view: 'nutshell-fields',
    }).sort(fieldSortFunction);

    if (filteredFields.length === 0) {
        return (
            <Body size='small' color='light'>
                No Nutshell fields available
            </Body>
        );
    }

    return (
        <div className={styles['grid']}>
            {filteredFields.map((field: FormSchemaProperty & {isDisabled: boolean}) => {
                const title = getTitle(field);
                const Icon = getIcon(field.entityType);
                const fieldKey = field.attributeId ?? field.name;
                const uniqueKey = `${field.name}-${field.entityType}`;

                if (field.isDisabled) {
                    const disabledText = getDisabledText(
                        fieldsForDuplicateHelpers,
                        field.name,
                        field.entityType
                    );

                    return (
                        <div key={uniqueKey} title={disabledText}>
                            <FieldCardPreview
                                Icon={Icon}
                                label={title}
                                isDragging={false}
                                isDisabled={true}
                            />
                        </div>
                    );
                }

                // We pass along any existing Nutshell custom fields as
                // 'nutshell-field' (long text, dropdowns, etc.) while any
                // Nutshell non-custom fields (person name, company address,
                // etc.) will continue to serve as a generic 'input'
                const dragData = field.isCustomField
                    ? {
                          kind: 'existing-custom' as const,
                          input: {
                              fieldKey,
                              displayType: field.displayType,
                              entityType: field.entityType,
                          },
                      }
                    : {
                          kind: 'input' as const,
                          input: {
                              fieldKey: field.name,
                              displayType: field.displayType,
                              entityType: field.entityType,
                          },
                      };

                return (
                    <AddableFieldCard
                        key={uniqueKey}
                        id={uniqueKey}
                        icon={Icon}
                        label={title}
                        dragData={dragData}
                    />
                );
            })}
        </div>
    );
}
